import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { SectionPanel, ThemedButton } from "@/App/components";
import { Body, Paragraph, Semibold, Subheading } from "capitalrx-components-2.0/lib/typography";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Icon, useAlert } from "capitalrx-components-2.0/lib/general";
import { PharmacyWithFavorite } from "./PharmacySearchCard";
import { useCreateFavoritePharmacyMutation, useUpdateFavoritePharamcyMutation } from "@/App/api/pharmacy";
import { useNavigateClient } from "@/App/hooks";
import { getRoundedPharmacyDistance } from "@/App/utils/utils";
const TRANSLATION_PATH = "DASHBOARD.recentActivityDashboard.findNearbyPharmacies";

export interface PharmacyResultProps {
  readonly pharmacy: PharmacyWithFavorite;
  readonly buildSearchParams: () => string;
}

export const PharmacyResultCard: FC<PharmacyResultProps> = ({ pharmacy, buildSearchParams }) => {
  const { t: translate } = useTranslation();
  const alert = useAlert();
  const navigate = useNavigateClient();

  const { mutateAsync: updateFavoritePharmacy, isPending: isUpdatingFavoritePharmacy } = useUpdateFavoritePharamcyMutation();
  const { mutateAsync: createFavoritePharmacy, isPending: isCreatingFavoritePharmacy } = useCreateFavoritePharmacyMutation();

  const toggleFavoritePharmacy = useCallback(async () => {
    try {
      if (pharmacy.isFavorite) {
        await updateFavoritePharmacy(pharmacy.provider_id);
        pharmacy.isFavorite = false;
      } else {
        await createFavoritePharmacy(pharmacy.provider_id);
        pharmacy.isFavorite = true;
      }
    } catch (e) {
      console.error(e);
      const errorMessage = e?.data?.message
      || translate(`${TRANSLATION_PATH}.${pharmacy.isFavorite ? "remove" : "set"}FavoriteError`);
      alert({
        message: errorMessage,
        type: "error",
        style: "toast",
      });
    }
  }, [pharmacy]);

  return (
    <SectionPanel>
      <Stack spacing="none">
        <Shelf spacing="none">
          <Body style={{ color: "#60646D", textTransform: "uppercase" }}><Semibold>{pharmacy.address_line_1} {pharmacy.address_line_2}</Semibold></Body>
          <Shelf.Spacer />
          <ThemedButton
            disabled={isUpdatingFavoritePharmacy || isCreatingFavoritePharmacy}
            aria-label={pharmacy.isFavorite ? translate(`${TRANSLATION_PATH}.removeFavorite`) : translate(`${TRANSLATION_PATH}.saveFavorite`)}
            onClick={toggleFavoritePharmacy}
            style={{ padding: 0 }}
            appearance="text">
            <Icon name={pharmacy.isFavorite ? "star-filled" : "star-empty"} />
          </ThemedButton>
        </Shelf>
        <Shelf spacing="none">
          <Subheading style={{ margin: 0, textTransform: "uppercase" }}>{pharmacy.name}</Subheading>
          <Shelf.Spacer />
          <Paragraph>{getRoundedPharmacyDistance(pharmacy)} {translate(`${TRANSLATION_PATH}.milesAbbreviation`)}</Paragraph>
        </Shelf>
      </Stack>
      <ThemedButton
        onClick={() => navigate(`nearby-pharmacy/pharmacy-details/${pharmacy.provider_id}${buildSearchParams()}`)}
        style={{ marginTop: "1em" }}
        icon="arrow-right"
        iconPosition="right"
        appearance="text">
          {translate(`${TRANSLATION_PATH}.viewDetailsButton`)}
      </ThemedButton>
    </SectionPanel>
  );
};
